import { useEffect, useCallback } from 'react';

/**
 * useModalBehavior - Reusable modal behavior hook
 *
 * Provides common modal behaviors like ESC key handling, overlay click handling, and body scroll lock.
 * This hook extracts common modal patterns to ensure consistency across all modals.
 *
 * @param {boolean} isOpen - Whether the modal is currently open
 * @param {Function} onClose - Callback function to close the modal
 * @param {Object} options - Configuration options
 * @param {boolean} options.closeOnEscape - Enable ESC key to close modal (default: true)
 * @param {boolean} options.closeOnOverlayClick - Enable clicking overlay to close modal (default: true)
 * @param {boolean} options.lockBodyScroll - Lock body scroll when modal is open (default: true)
 *
 * @returns {Object} - { handleOverlayClick } helper function
 *
 * @example
 * const MyModal = ({ isOpen, onClose }) => {
 *   const { handleOverlayClick } = useModalBehavior(isOpen, onClose);
 *
 *   return (
 *     <div className="modal-overlay" onClick={handleOverlayClick}>
 *       <div className="modal-content">
 *         // Modal content...
 *       </div>
 *     </div>
 *   );
 * };
 */
export const useModalBehavior = (isOpen, onClose, options = {}) => {
  const {
    closeOnEscape = true,
    closeOnOverlayClick = true,
    lockBodyScroll = true
  } = options;

  // ESC key handler
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, closeOnEscape]);

  // Body scroll lock
  useEffect(() => {
    if (!isOpen || !lockBodyScroll) return;

    // Save original body overflow value (default to empty string if not set)
    const bodyElement = document.body;
    const originalOverflow = bodyElement.style.overflow || '';

    // Lock scroll
    bodyElement.style.overflow = 'hidden';

    // Cleanup: restore original overflow when modal closes
    // If original was empty/unset, restore to 'auto' to ensure scrolling works
    return () => {
      bodyElement.style.overflow = originalOverflow || 'auto';
    };
  }, [isOpen, lockBodyScroll]);

  // Overlay click handler
  const handleOverlayClick = useCallback((e) => {
    // Only close if clicking directly on the overlay (not its children)
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose, closeOnOverlayClick]);

  return {
    handleOverlayClick
  };
};
